import request from '@/api/request'

export interface WorkOrderItem {
  id?: number
  workOrderId?: number
  productId: number
  productCode?: string
  productName?: string
  spec?: string
  unit?: string
  quantity: number
  unitPrice: number
  discountedPrice: number
  amount?: number
  costPrice?: number
  warehouseId?: number
  locationId?: number
  remark?: string
}

export interface WorkOrderPayment {
  id: number
  paymentNo: string
  amount: number
  paymentMethod: string
  transactionNo?: string
  remark?: string
  images?: string[]
  accountName?: string
  createdByName?: string
  createdAt: string
}

export interface WorkOrder {
  id: number
  orderNo: string
  customerId: number
  customerName?: string
  customerPhone?: string
  customerAddress?: string
  salesOrderId?: number
  salesOrderNo?: string
  salesNames?: string | null
  outboundId?: number
  outboundNo?: string
  outboundStatus?: string
  productId?: number
  productName?: string
  type: 'install' | 'repair' | 'maintenance' | 'measure' | 'delivery'
  status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'cancelled'
  installerEmployeeId?: number
  installerName?: string
  scheduleId?: number
  scheduledAt?: string
  completedAt?: string
  participants?: { employeeId: number; employeeName: string }[]
  participantIds?: number[]

  installLocation?: string
  installRemark?: string
  serviceFee?: number
  serviceCommission?: number
  serviceDescription?: string
  source: 'internal' | 'customer'
  faultType?: string
  expectedDate?: string
  expectedTimeSlot?: 'am' | 'pm' | 'any'
  acceptResult?: 'normal' | 'abnormal'
  acceptRemark?: string
  acceptedAt?: string
  // 维修工单扩展字段
  isWarranty?: number
  chargeType?: 'onsite' | 'credit'
  materialAmount?: number
  totalAmount?: number
  paidAmount?: number
  approvedBy?: number
  approvedAt?: string
  items?: WorkOrderItem[]
  payments?: WorkOrderPayment[]
  images?: WorkOrderImage[]
  createdAt?: string
  updatedAt?: string
}

export interface WorkOrderProgressRecord {
  id: number
  workOrderId: number
  outboundId?: number
  productId: number
  productName?: string
  completedQty: number
  completionDate: string
  photos?: string[]
  remark?: string
  createdBy?: number
  createdByName?: string
  createdAt?: string
  workOrderNo?: string
  installerName?: string
}

export interface WorkOrderProgressData {
  completedQty: number
  completionDate: string
  photos?: string[]
  remark?: string
}

export interface WorkOrderProgressResult {
  outboundQty: number
  completedTotal: number
  remainingQty: number
  records: WorkOrderProgressRecord[]
}

export interface WorkOrderImage {
  id: number
  workOrderId: number
  imageUrl: string
  thumbnailUrl?: string
  sort: number
  uploadedBy: 'customer' | 'staff'
  uploadedAt: string
}

export interface WorkOrderQuery {
  page?: number
  pageSize?: number
  status?: string
  type?: string
  customerId?: number
  installerEmployeeId?: number
  participantIds?: number[]
  startDate?: string
  endDate?: string
  keyword?: string
  salesOrderNo?: string
  customer?: string
  productKeyword?: string
  installLocation?: string
  mine?: string
}

export interface CreateWorkOrderData {
  orderNo?: string
  customerId: number
  salesOrderId?: number
  outboundId?: number
  outboundNo?: string
  productId?: number
  type?: 'install' | 'repair' | 'maintenance' | 'measure' | 'delivery'
  installerEmployeeId?: number
  installerEmployeeIds?: number[]
  scheduledAt?: string
  installLocation?: string
  installRemark?: string
  serviceFee?: number
  serviceCommission?: number
  serviceDescription?: string
  source?: 'internal' | 'customer'
  customerInfo?: Record<string, any>
  faultType?: string
  expectedDate?: string
  expectedTimeSlot?: 'am' | 'pm' | 'any'
  participantIds?: number[]
  // 维修工单扩展
  isWarranty?: number
  chargeType?: 'onsite' | 'credit'
  warehouseId?: number
  items?: WorkOrderItem[]
}

export interface ReceiveWorkOrderPaymentData {
  accountId: number
  amount: number
  paymentMethod?: string
  transactionNo?: string
  remark?: string
  images?: string[]
}

export interface CompleteWorkOrderData {
  photos?: string[]
  remark?: string
  caseImages?: string[]
}

// 查询工单列表
export function getWorkOrderList(params: WorkOrderQuery) {
  return request.get('/work-orders', { params })
}

// 查询我的待安装列表
export function getMyWorkOrderList(params?: WorkOrderQuery) {
  return request.get('/work-orders/my-list', { params })
}

export interface MyAppointment {
  scheduleId: number
  salesOrderId: number
  salesOrderNo: string
  planDate: string
  planStartTime?: string
  planEndTime?: string
  isAllDay?: number
  timeSlot?: string
  location?: string
  customerName?: string
  customerPhone?: string
  customerAddress?: string
  productSummary?: string
}

// 我的预约安装（出库前的预约日程，确认出库生成工单后自动消失）
export function getMyAppointments() {
  return request.get('/work-orders/my-appointments')
}

// 查询工单详情
export function getWorkOrderDetail(id: number) {
  return request.get(`/work-orders/${id}`)
}

// 创建工单
export function createWorkOrder(data: CreateWorkOrderData) {
  return request.post('/work-orders', data)
}

// 更新工单
export function updateWorkOrder(id: number, data: Partial<CreateWorkOrderData>) {
  return request.put(`/work-orders/${id}`, data)
}

// 接单
export function acceptWorkOrder(id: number, installerEmployeeId: number) {
  return request.post(`/work-orders/${id}/accept`, { installerEmployeeId })
}

// 完成安装
export function completeWorkOrder(id: number, data: CompleteWorkOrderData) {
  return request.post(`/work-orders/${id}/complete`, data)
}

// 录入进度
export function recordWorkOrderProgress(id: number, data: WorkOrderProgressData) {
  return request.post(`/work-orders/${id}/progress`, data)
}

// 查询进度记录
export function getWorkOrderProgress(id: number) {
  return request.get(`/work-orders/${id}/progress`)
}

// 上传照片
export function uploadWorkOrderPhotos(id: number, photos: string[]) {
  return request.post(`/work-orders/${id}/photos`, { photos })
}

// 获取照片
export function getWorkOrderPhotos(id: number) {
  return request.get(`/work-orders/${id}/photos`)
}

// 取消工单
export function cancelWorkOrder(id: number) {
  return request.post(`/work-orders/${id}/cancel`)
}

// 撤销维修工单完工（仅 admin，completed → assigned）
export function revokeCompleteWorkOrder(id: number) {
  return request.post(`/work-orders/${id}/revoke-complete`)
}

// 催办工单（通知安装人员+参与人）
export function remindWorkOrder(id: number) {
  return request.post(`/work-orders/${id}/remind`)
}

// 审核维修工单（有物料明细时自动生成维修用料出库草稿单）
export function approveWorkOrder(id: number) {
  return request.post(`/work-orders/${id}/approve`)
}

// 撤销维修工单审核（物料费用恢复可编辑，出库草稿联动取消）
export function revokeApproveWorkOrder(id: number) {
  return request.post(`/work-orders/${id}/revoke-approve`)
}

// 维修收款
export function receiveWorkOrderPayment(id: number, data: ReceiveWorkOrderPaymentData) {
  return request.post(`/work-orders/${id}/receive-payment`, data)
}

// 删除工单
export function deleteWorkOrder(id: number) {
  return request.delete(`/work-orders/${id}`)
}

// 获取统计（支持列表同款筛选参数，status 由后端剔除）
export function getWorkOrderStats(params?: Partial<WorkOrderQuery>) {
  return request.get('/work-orders/stats', { params })
}
